import type { Edge, Node, NodeGroup } from '../../types';
import type { NodeDirection } from '../../utils/socketLayout';
import {
  layoutFlowGraph,
  translateLayout,
  type FlowLayoutResult,
  type RankAxis,
} from '../../utils/elkLayout';

export type { RankAxis };

export interface LayoutViewport {
  x: number;
  y: number;
  scale: number;
}

/**
 * Automatic layout for flow diagrams.
 *
 * This is a thin shell around `elkLayout`: it picks the rank axis when the
 * caller has no opinion and centres the finished layout on what the author is
 * looking at. Everything else - ordering, frame placement, ports, routing - is
 * decided in one ELK run, which is the point. Mermaid import and Beautify both
 * come through here, so the two produce the same picture from the same graph.
 */
export class LayoutService {
  static async layout(
    nodes: Node[],
    edges: Edge[],
    groups: NodeGroup[],
    viewport: LayoutViewport,
    windowSize: { width: number; height: number },
    options?: { rankAxis?: RankAxis },
  ): Promise<FlowLayoutResult> {
    const axis = options?.rankAxis ?? inferRankAxis(nodes);
    const laid = await layoutFlowGraph(nodes, edges, groups, { axis });
    const bounds = laid.bounds;
    if (!bounds) return laid;

    const scale = Math.max(viewport.scale, 0.001);
    const centreX = (windowSize.width / 2 - viewport.x) / scale;
    const centreY = (windowSize.height / 2 - viewport.y) / scale;
    return translateLayout(
      laid,
      centreX - bounds.width / 2 - bounds.x,
      centreY - bounds.height / 2 - bounds.y,
    );
  }
}

/**
 * Which way the diagram flows. Architecture nodes carry their socket direction,
 * so a page whose nodes are mostly top-to-bottom is laid out that way; anything
 * else reads left to right, which is how the sockets on a default node sit.
 */
function inferRankAxis(nodes: Node[]): RankAxis {
  let ttb = 0;
  let other = 0;
  for (const node of nodes) {
    const direction = node.data?.direction as NodeDirection | undefined;
    if (direction === 'ttb') ttb += 1;
    else other += 1;
  }
  return ttb > other ? 'ttb' : 'ltr';
}
